/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Chat logs are the heaviest part of a project document: each turn can carry a
// large `reasoning` trace, and a long AI conversation accumulates them. Stored
// raw, the project doc can exceed Firestore's hard 1 MiB per-document limit and
// the save is rejected outright. Reasoning is verbose prose that gzip-deflates
// ~4-6x, so instead of a separate document (which would just move the same
// ceiling) we compress the whole chat array and stash it as a single Firestore
// `Bytes` field on the project doc. Firestore counts the *compressed* byte
// length toward the limit (binary, no base64 inflation), so a realistic
// conversation lands comfortably under 1 MiB while keeping every reasoning
// trace fully recoverable on load.

import { Bytes } from 'firebase/firestore';
import { gzipSync, gunzipSync, strToU8, strFromU8 } from 'fflate';
import { ChatMessageInterface } from '../types';

// Firestore field name for the gzip-compressed chat payload. Saved docs carry
// this instead of the plain `chatMessages` array; legacy docs (pre-compression)
// still carry the plain array and are read via the fallback in
// reconstructProjectState.
export const CHAT_GZ_FIELD = 'chatMessagesGz';

// Keep the whole project document safely under Firestore's 1,048,576-byte hard
// limit. The scene (molecules, params, view) is written first; whatever budget
// remains is spent on the compressed chat (see buildChatField). The ~48 KB of
// headroom absorbs field names and Firestore's own per-field overhead, which
// the raw JSON length estimate doesn't capture.
export const SAFE_DOC_BYTES = 1_000_000;

export const encodeChatMessages = (messages: ChatMessageInterface[]): Uint8Array =>
  gzipSync(strToU8(JSON.stringify(messages)));

export const decodeChatMessages = (bytes: Uint8Array): ChatMessageInterface[] => {
  const parsed = JSON.parse(strFromU8(gunzipSync(bytes)));
  return Array.isArray(parsed) ? parsed : [];
};

// Coerce whatever the compressed field deserialized to into a Uint8Array: a
// Firestore `Bytes` instance (the normal getDoc path), a raw Uint8Array, or an
// index-map / array that a JSON round-trip may have produced. Returns null when
// the value is absent or not decodable, so callers fall back to legacy inline
// chat.
export const toUint8 = (v: any): Uint8Array | null => {
  if (!v) return null;
  if (typeof v.toUint8Array === 'function') return v.toUint8Array(); // firestore Bytes
  if (v instanceof Uint8Array) return v;
  if (Array.isArray(v)) return Uint8Array.from(v);
  if (typeof v === 'object') {
    // {0: .., 1: ..} index map from an accidental JSON serialization of bytes.
    const keys = Object.keys(v);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) return Uint8Array.from(keys.map((k) => v[k]));
  }
  return null;
};

// Compress the chat for storage, trimming only if the gzip'd payload still
// exceeds the byte budget left over after the scene. Trimming is a rare
// backstop, applied to COPIES so the live in-session chat keeps its full
// reasoning: first drop reasoning traces oldest-first (display-only, already
// folded into chatSummary for the model), then, only if still over, drop whole
// oldest turns. Returns the Bytes value to write (null when there is no chat)
// plus a human-readable note when anything was trimmed.
export const buildChatField = (
  messages: ChatMessageInterface[] | undefined,
  budgetBytes: number,
): { value: Bytes | null; note: string | null } => {
  if (!messages || messages.length === 0) return { value: null, note: null };

  let bytes = encodeChatMessages(messages);
  if (bytes.length <= budgetBytes) return { value: Bytes.fromUint8Array(bytes), note: null };

  // 1) Strip reasoning traces, oldest first.
  const working = messages.map((m) => ({ ...m }));
  let droppedReasoning = 0;
  for (let i = 0; i < working.length && bytes.length > budgetBytes; i++) {
    if (working[i].reasoning !== undefined || working[i].reasoningDurationMs !== undefined) {
      delete working[i].reasoning;
      delete working[i].reasoningDurationMs;
      droppedReasoning++;
      bytes = encodeChatMessages(working);
    }
  }
  // 2) Still over budget: drop whole oldest turns, keeping at least the newest.
  let droppedMessages = 0;
  while (bytes.length > budgetBytes && working.length > 1) {
    working.shift();
    droppedMessages++;
    bytes = encodeChatMessages(working);
  }

  const parts: string[] = [];
  if (droppedReasoning) parts.push(`${droppedReasoning} reasoning trace${droppedReasoning > 1 ? 's' : ''}`);
  if (droppedMessages) parts.push(`${droppedMessages} oldest message${droppedMessages > 1 ? 's' : ''}`);
  const note = parts.length
    ? `The chat log was too large to save in full; trimmed ${parts.join(' and ')} from the saved copy.`
    : null;
  return { value: Bytes.fromUint8Array(bytes), note };
};
